import { socket } from '../../contexts/SocketIo';
import { soundManager } from '../../utils/soundManager';
import {
  upsertConversation,
  addMessage,
  markParticipantAsRead,
  updateUserPresence,
  updateMessageReactions,
  removeConversation,
  markChatAsRead,
  updateChatPin,
  updateChatMode,
  setCall,
  resetCall,
  setActiveGroupCall,
  setSecondaryCall,
  resetSecondaryCall,
} from '../slices/internalChat';

// Event bus for WebRTC signaling (UI components can listen to this)
// Simple vanilla implementation to avoid node dependencies
class SimpleEmitter {
  listeners = {};

  on(event, fn) {
    if (!this.listeners[event]) this.listeners[event] = [];
    this.listeners[event].push(fn);
  }

  off(event, fn) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter((l) => l !== fn);
  }

  emit(event, data) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach((fn) => fn(data));
  }
}

export const callEvents = new SimpleEmitter();

const chatEventsMiddleware = (store) => {
  // Escuta eventos globais
  socket.on('internal_chat:new', (chat) => {
    store.dispatch(upsertConversation(chat));
  });

  socket.on('internal_chat:message', ({ chatId, message }) => {
    const state = store.getState();
    const activeChatId = state.internalChat.ui.activeConversationId;
    const currentUserId = window.localStorage.getItem('usr_id');

    store.dispatch(addMessage({ conversationId: chatId, message }));

    // Tocar som se a mensagem não for do próprio usuário
    // e se o som estiver habilitado no estado (opcional, por padrão tocamos)
    const senderId = message.senderId._id || message.senderId;
    if (String(senderId) !== String(currentUserId)) {
      soundManager.playSound('internal_message', message._id);
    }

    // Se a mensagem for para o chat ativo, marca como lida imediatamente
    if (chatId === activeChatId) {
      store.dispatch(markChatAsRead(chatId));
    }
  });

  socket.on('internal_chat:read', ({ chatId, userId }) => {
    store.dispatch(markParticipantAsRead({ conversationId: chatId, userId }));
  });

  socket.on('user:status', ({ userId, online, lastSeenAt }) => {
    store.dispatch(updateUserPresence({ userId, online, lastSeenAt }));
  });

  socket.on('internal_chat:reaction', ({ messageId, reactions }) => {
    store.dispatch(updateMessageReactions({ messageId, reactions }));
  });

  socket.on('internal_chat:removed', ({ chatId }) => {
    store.dispatch(removeConversation(chatId));
  });

  socket.on('internal_chat:update', (chat) => {
    store.dispatch(upsertConversation(chat));
  });

  socket.on('internal_chat:pinned', (data) => {
    store.dispatch(updateChatPin({ chatId: data.chatId, pinnedMessage: data.pinnedMessage }));
  });

  socket.on('internal_chat:mode_changed', (data) => {
    store.dispatch(updateChatMode({ chatId: data.chatId, mode: data.mode }));
  });

  // --- CALL SIGNALING EVENTS ---

  socket.on('internal_chat:call_incoming', (data) => {
    const state = store.getState();
    const callState = state.internalChat.call;

    const { conversations } = state.internalChat;
    const chat = conversations.byId[data.chatId];

    let partner = null;
    if (chat) {
      if (chat.type === 'group') {
        partner = { _id: chat._id, name: chat.name, avatar: chat.avatar };
      } else {
        const other = chat.participants.find((p) => String(p.userId._id || p.userId) === String(data.fromId));
        partner = other?.userId || { _id: data.fromId, name: 'Usuário' };
      }
    }

    // Se o usuário não estiver idle (já em ligação ou tocando), empurra como Secondary (Espera)
    if (callState.status !== 'idle') {
      store.dispatch(
        setSecondaryCall({
          chatId: data.chatId,
          type: data.type,
          partner,
          participantIds: data.participantIds || [],
        })
      );
      console.log(`[WebRTC] Chamada secundária recebida em espera de ${partner?.name || data.fromId}.`);
      return; // Impede que o Redux propague o dispatch setCall e destrua o modal atual
    }

    store.dispatch(
      setCall({
        status: 'ringing',
        isIncoming: true,
        chatId: data.chatId,
        type: data.type,
        partner,
        participantIds: data.participantIds || [],
      })
    );
  });

  socket.on('internal_chat:call_offer', (data) => {
    callEvents.emit('offer', data);
  });

  socket.on('internal_chat:call_answer', (data) => {
    callEvents.emit('answer', data);
  });

  socket.on('internal_chat:active_calls_sync', (data) => {
    store.dispatch(
      setActiveGroupCall({
        chatId: data.chatId,
        activeCall: data.activeCall,
      })
    );
  });

  socket.on('internal_chat:call_user_joined', (data) => {
    callEvents.emit('user-joined', data);
  });

  socket.on('internal_chat:call_ice_candidate', (data) => {
    callEvents.emit('ice-candidate', data);
  });

  socket.on('internal_chat:screen_share_status', (data) => {
    callEvents.emit('screen-share-status', data);
  });

  socket.on('internal_chat:call_hangup', (data) => {
    const state = store.getState();
    const callState = state.internalChat.call;
    const { secondaryCall } = state.internalChat;

    if (secondaryCall && secondaryCall.chatId === data.chatId) {
      store.dispatch(resetSecondaryCall());
    }

    if (callState.status !== 'idle' && callState.chatId === data.chatId) {
      const is1v1 = !callState.participantIds || callState.participantIds.length <= 2;
      const isCallerDrop = String(data.fromId) === String(callState.partner?._id);

      // Em caso de Call Waiting ou Calling: se é 1v1 ou a Própria Origem cancelou a call de grupo, mata a ui.
      if (callState.status === 'ringing' || callState.status === 'calling') {
        if (is1v1 || isCallerDrop) {
          store.dispatch(resetCall());
          soundManager.stopRinging();
          return;
        }
      } else if (callState.status === 'active') {
        if (is1v1) store.dispatch(resetCall());
      }
    }

    callEvents.emit('hangup', data);
  });

  socket.on('internal_chat:call_rejected', (data) => {
    const state = store.getState();
    const callState = state.internalChat.call;
    if (callState.status !== 'active' && callState.chatId === data.chatId) {
      if (!callState.participantIds || callState.participantIds.length <= 2) {
        store.dispatch(resetCall());
      }
    }
    callEvents.emit('rejected', data);
  });

  socket.on('internal_chat:call_accepted_elsewhere', () => {
    store.dispatch(resetCall());
  });

  return (next) => (action) => next(action);
};

export default chatEventsMiddleware;
